import dayjs from 'dayjs';
import type { HealthSnapshot, StatsSnapshot } from '@api/api';

export interface Point {
  t: number;
  v: number;
}

/**
 * Turns stats snapshots (every 15 min; rides.today / revenue.today are running totals that reset
 * at midnight) into per-bucket sums: per hour for short ranges, per day otherwise.
 */
export function perBucket(snaps: StatsSnapshot[], pick: (s: StatsSnapshot) => number, unit: 'hour' | 'day'): Point[] {
  const sorted = [...snaps].sort((a, b) => a.ts.localeCompare(b.ts));
  if (unit === 'day') {
    const m = new Map<string, Point>();
    for (const s of sorted) {
      const d = dayjs(s.ts);
      const key = d.format('YYYY-MM-DD');
      const v = pick(s);
      const cur = m.get(key);
      if (!cur || v > cur.v) m.set(key, { t: d.startOf('day').valueOf(), v: Math.max(v, cur?.v ?? 0) });
    }
    return [...m.values()];
  }
  const m = new Map<number, Point>();
  let prev: { day: string; v: number } | null = null;
  for (const s of sorted) {
    const d = dayjs(s.ts);
    const v = pick(s);
    const day = d.format('YYYY-MM-DD');
    const inc = !prev || prev.day !== day || v < prev.v ? v : v - prev.v;
    prev = { day, v };
    const key = d.startOf('hour').valueOf();
    const cur = m.get(key);
    m.set(key, { t: key, v: (cur?.v ?? 0) + inc });
  }
  // the first point has no baseline: drop it unless it's the only one
  const out = [...m.values()];
  return out.length > 1 ? out.slice(1) : out;
}

/** Gauge-like values (available scooters, riders total): last value per bucket. */
export function lastPerBucket(snaps: StatsSnapshot[], pick: (s: StatsSnapshot) => number, unit: 'hour' | 'day'): Point[] {
  const m = new Map<number, Point>();
  for (const s of [...snaps].sort((a, b) => a.ts.localeCompare(b.ts))) {
    const key = dayjs(s.ts).startOf(unit).valueOf();
    m.set(key, { t: key, v: pick(s) });
  }
  return [...m.values()];
}

/** Share of checks where the clone answered (ok or degraded), in %. */
export function uptimePct(snaps: HealthSnapshot[]): number | null {
  const known = snaps.filter((s) => s.health !== 'unknown' && s.health !== 'provisioning' && s.health !== 'stopped');
  if (!known.length) return null;
  const up = known.filter((s) => s.health === 'ok' || s.health === 'degraded').length;
  return (up / known.length) * 100;
}

export function sumPoints(p: Point[]): number {
  return p.reduce((s, x) => s + x.v, 0);
}
